import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import libraryService from "../../services/libraryService";

const MAX_COVER_SIZE = 2 * 1024 * 1024;
const EMPTY_FORM = {
    title: "",
    author: "",
    publisher: "",
    isbn: "",
    category: "",
    edition: "",
    language: "English",
    shelfLocation: "",
    totalCopies: 1,
    price: "",
    coverImageUrl: "",
    coverImagePublicId: "",
    coverImageDataUrl: "",
};

const CATEGORY_OPTIONS = ["Textbook", "Reference", "Journal", "Fiction", "Other"];

function BookField({
    label,
    name,
    required = false,
    type = "text",
    placeholder = "",
    value,
    onChange,
    error,
}) {
    return (
        <div className="form-group">
            <label className="form-label">
                {label}
                {required && <span style={{ color: "var(--danger)" }}> *</span>}
            </label>
            <input
                className={`form-control ${error ? "error" : ""}`}
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
            />
            {error && <span className="form-error">{error}</span>}
        </div>
    );
}

function BookSelectField({ label, name, options, required = false, value, onChange, error }) {
    return (
        <div className="form-group">
            <label className="form-label">
                {label}
                {required && <span style={{ color: "var(--danger)" }}> *</span>}
            </label>
            <select className={`form-control ${error ? "error" : ""}`} name={name} value={value} onChange={onChange}>
                <option value="">Select {label}</option>
                {options.map((option) => (
                    <option key={option} value={option}>
                        {option}
                    </option>
                ))}
            </select>
            {error && <span className="form-error">{error}</span>}
        </div>
    );
}

export default function BookForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);

    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEdit);
    const [removeCover, setRemoveCover] = useState(false);

    useEffect(() => {
        if (!isEdit) return;

        (async () => {
            try {
                const res = await libraryService.getBookById(id);
                const book = res.data.book || res.data; // handles either { book, issues } or plain book shape
                setForm({
                    title: book.title || "",
                    author: book.author || "",
                    publisher: book.publisher || "",
                    isbn: book.isbn || "",
                    category: book.category || "",
                    edition: book.edition || "",
                    language: book.language || "English",
                    shelfLocation: book.shelfLocation || "",
                    totalCopies: book.totalCopies ?? 1,
                    price: book.price ?? "",
                    coverImageUrl: book.coverImageUrl || "",
                    coverImagePublicId: book.coverImagePublicId || "",
                    coverImageDataUrl: "",
                });
            } catch (err) {
                toast.error(err.message);
                navigate("/library/books");
            } finally {
                setFetching(false);
            }
        })();
    }, [id, isEdit, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };

    const validate = () => {
        const nextErrors = {};
        if (!form.title.trim()) nextErrors.title = "Book title is required";
        if (!form.author.trim()) nextErrors.author = "Author is required";
        if (!form.totalCopies || Number(form.totalCopies) < 1) {
            nextErrors.totalCopies = "At least 1 copy is required";
        }
        return nextErrors;
    };

    const readFileAsDataUrl = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(new Error("Failed to read image file."));
            reader.readAsDataURL(file);
        });

    const handleCoverChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            toast.error("Please choose a valid image file.");
            e.target.value = "";
            return;
        }
        if (file.size > MAX_COVER_SIZE) {
            toast.error("Cover image must be 2 MB or smaller.");
            e.target.value = "";
            return;
        }

        try {
            const dataUrl = await readFileAsDataUrl(file);
            setForm((prev) => ({ ...prev, coverImageUrl: dataUrl, coverImageDataUrl: dataUrl }));
            setRemoveCover(false);
        } catch (err) {
            toast.error(err.message);
        } finally {
            e.target.value = "";
        }
    };

    const clearCover = () => {
        setForm((prev) => ({ ...prev, coverImageUrl: "", coverImagePublicId: "", coverImageDataUrl: "" }));
        setRemoveCover(Boolean(isEdit));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const nextErrors = validate();

        if (Object.keys(nextErrors).length) {
            setErrors(nextErrors);
            toast.error("Please fix the errors before saving.");
            return;
        }

        setLoading(true);
        try {
            const payload = {
                ...form,
                totalCopies: Number(form.totalCopies),
                price: form.price === "" ? 0 : Number(form.price),
                removeCoverImage: removeCover,
            };
            if (isEdit) {
                await libraryService.updateBook(id, payload);
                toast.success("Book updated successfully.");
            } else {
                await libraryService.createBook(payload);
                toast.success("Book added successfully.");
            }
            navigate("/library/books");
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <div className="card">
                <div className="loading-center">
                    <div className="spinner" />
                    Loading book...
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">{isEdit ? "Edit Book" : "Add Book"}</h1>
                    <p className="page-subtitle">Maintain the library catalog — title, author, copies, and location.</p>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="card">
                    <div className="card-body">
                        <div className="form-grid">
                            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                <label className="form-label">Cover Image</label>
                                <div style={{ display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
                                    <div
                                        style={{
                                            width: 90,
                                            height: 120,
                                            borderRadius: 8,
                                            overflow: "hidden",
                                            border: "1px solid var(--border)",
                                            background: "#f8fafc",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "var(--text-muted)",
                                            fontSize: 11,
                                            fontWeight: 600,
                                        }}
                                    >
                                        {form.coverImageUrl ? (
                                            <img
                                                src={form.coverImageUrl}
                                                alt="Cover"
                                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                            />
                                        ) : (
                                            <span>No Cover</span>
                                        )}
                                    </div>
                                    <div style={{ display: "grid", gap: "0.55rem" }}>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="form-control"
                                            onChange={handleCoverChange}
                                            style={{ maxWidth: 320 }}
                                        />
                                        <span className="form-hint">JPG, PNG or WEBP up to 2 MB. Optional.</span>
                                        {form.coverImageUrl && (
                                            <div>
                                                <button type="button" className="btn btn-outline btn-sm" onClick={clearCover}>
                                                    Remove Cover
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <BookField
                                label="Title"
                                name="title"
                                required
                                placeholder="e.g. Remington's Pharmaceutical Sciences"
                                value={form.title}
                                onChange={handleChange}
                                error={errors.title}
                            />
                            <BookField
                                label="Author"
                                name="author"
                                required
                                placeholder="e.g. Joseph P. Remington"
                                value={form.author}
                                onChange={handleChange}
                                error={errors.author}
                            />
                            <BookField
                                label="Publisher"
                                name="publisher"
                                placeholder="e.g. Lippincott"
                                value={form.publisher}
                                onChange={handleChange}
                                error={errors.publisher}
                            />
                            <BookField
                                label="ISBN"
                                name="isbn"
                                placeholder="e.g. 978-0-857-11562-3"
                                value={form.isbn}
                                onChange={handleChange}
                                error={errors.isbn}
                            />
                            <BookSelectField
                                label="Category"
                                name="category"
                                options={CATEGORY_OPTIONS}
                                value={form.category}
                                onChange={handleChange}
                                error={errors.category}
                            />
                            <BookField
                                label="Edition"
                                name="edition"
                                placeholder="e.g. 22nd Edition"
                                value={form.edition}
                                onChange={handleChange}
                                error={errors.edition}
                            />
                            <BookField
                                label="Language"
                                name="language"
                                value={form.language}
                                onChange={handleChange}
                                error={errors.language}
                            />
                            <BookField
                                label="Shelf Location"
                                name="shelfLocation"
                                placeholder="e.g. Rack A-3"
                                value={form.shelfLocation}
                                onChange={handleChange}
                                error={errors.shelfLocation}
                            />
                            <BookField
                                label="Total Copies"
                                name="totalCopies"
                                type="number"
                                required
                                value={form.totalCopies}
                                onChange={handleChange}
                                error={errors.totalCopies}
                            />
                            <BookField
                                label="Price (₹)"
                                name="price"
                                type="number"
                                placeholder="Used for lost-book charges"
                                value={form.price}
                                onChange={handleChange}
                                error={errors.price}
                            />
                        </div>

                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "1.25rem" }}>
                            <button type="button" className="btn btn-outline" onClick={() => navigate("/library/books")}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={loading}>
                                {loading ? "Saving..." : isEdit ? "Update Book" : "Add Book"}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}